import { Badge } from "@chakra-ui/react/badge";
import { Box } from "@chakra-ui/react/box";
import { IconButton } from "@chakra-ui/react/button";
import { Flex } from "@chakra-ui/react/flex";
import { Text } from "@chakra-ui/react/text";

import { Tooltip } from "components/ui/tooltip";
import { format, isSameWeek, isToday, isYesterday, parseISO } from "date-fns";
import NotificationDomain from "entities/domain/notifications/domain";
import { useMarkNotificationRead } from "hooks/queries/use-notifications";
import React from "react";
import { LuCheck } from "react-icons/lu";
import { useAppSelector } from "redux/hooks";

interface NotificationItemProps {
  notification: NotificationDomain;
}

// A grouped entry (one per conversation) counts the messages since it was
// last read.
function getUnreadLabel(notification: NotificationDomain): string {
  return notification.itemCount > 1 ? `${notification.itemCount} NEW` : "NEW";
}

export default function NotificationItem({
  notification,
}: NotificationItemProps) {
  const { colorScheme } = useAppSelector((state) => state.theme);
  const markNotificationAsReadMutation = useMarkNotificationRead();

  const getDisplayDate = (n: NotificationDomain): string => {
    const latestAt = n.updatedAt ?? n.createdAt;
    const createdDate = parseISO(
      latestAt.includes("Z") ? latestAt : `${latestAt}Z`
    );

    if (isToday(createdDate)) {
      return format(createdDate, "'Today at' h:mm a");
    } else if (isYesterday(createdDate)) {
      return format(createdDate, "'Yesterday at' h:mm a");
    } else if (isSameWeek(createdDate, new Date())) {
      return format(createdDate, "EEEE 'at' h:mm a"); // Day of the week at time
    } else {
      return format(createdDate, "dd/MM/yyyy 'at' HH:mm");
    }
  };

  const handleBoxClick = () => {
    if (notification.deepLink) {
      markNotificationAsReadMutation.mutate(notification.id);
      window.location.href = notification.deepLink;
    }
  };

  return (
    <Box
      key={notification.id}
      p={4}
      mb={4}
      borderRadius="md"
      border="1px solid"
      borderColor="border.subtle"
      bg={notification.read ? "inherit" : "bg.subtle"}
      _hover={{ bg: "bg.muted" }}
      boxShadow="sm"
      onClick={handleBoxClick}
      cursor={notification.deepLink ? "pointer" : "default"}
      position="relative"
    >
      <Flex justifyContent="space-between" alignItems="center">
        <Box flex="1" mr={4}>
          <Flex alignItems="center" mb={2}>
            <Text fontWeight={notification.read ? "normal" : "bold"}>
              {notification.title}
            </Text>
          </Flex>
          <Text>{notification.message}</Text>
          <Text textStyle="caption" color="fg.subtle" fontStyle="italic">
            {getDisplayDate(notification)}
          </Text>
        </Box>
        <Flex alignItems="center">
          {!notification.read && (
            <Badge colorPalette="gray" borderRadius="full" px={2} py={1} mr={2}>
              {getUnreadLabel(notification)}
            </Badge>
          )}
          {!notification.read && (
            <Tooltip content="Mark as Read" aria-label="Mark as Read">
              <IconButton
                size="sm"
                colorPalette="green"
                onClick={(e) => {
                  e.stopPropagation();
                  markNotificationAsReadMutation.mutate(notification.id);
                }}
                aria-label="Mark as Read"
                // FREO: intentional circular icon-only button
                borderRadius="full"
                bg="transparent"
                _hover={{ bg: "gray.emphasized" }}
              >
                <LuCheck />
              </IconButton>
            </Tooltip>
          )}
        </Flex>
      </Flex>
    </Box>
  );
}
